import React, { useState, useEffect } from 'react';
import {
    Typography,
    Card,
    Table,
    Button,
    Space,
    Input,
    Select,
    Modal,
    Form,
    DatePicker,
    Divider,
    InputNumber,
    message,
    Popconfirm,
    Tag,
    Tooltip,
    Row,
    Col,
    Statistic,
    Drawer,
    Descriptions,
    Tabs,
    Empty,
    List,
    FloatButton,
    Grid,
    Radio,
    Alert,
    Checkbox,
    Spin
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    TruckOutlined,
    DollarOutlined,
    CalendarOutlined,
    UserOutlined,
    PrinterOutlined,
    FilterOutlined,
    CalculatorOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
    getPendingDeliveries,
    getPendingDeliveryById,
    createPendingDelivery,
    updatePendingDelivery,
    deletePendingDelivery,
    updateDeliveredQuantity
} from '../../services/pendingDeliveryService';
import { getCustomersForDropdown } from '../../services/contactService';
import { getProducts } from '../../services/productService';
import { generatePendingDeliveryPDF } from '../../services/pendingDeliveryPdfService';
import { getCompanySettings, getLogoUrl } from '../../services/settingsService';
import SignatureLayoutControls from '../../components/SignatureLayoutControls';
import useDocumentSignatureLayout from '../../hooks/useDocumentSignatureLayout';
import UnitsSelect from '../../components/UnitsSelect';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const PendingDeliveriesPage = () => {
    const [deliveryNotes, setDeliveryNotes] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [deliveryModalVisible, setDeliveryModalVisible] = useState(false);
    const [editingDeliveryNote, setEditingDeliveryNote] = useState(null);
    const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null);
    const [form] = Form.useForm();
    const [deliveryForm] = Form.useForm();

    // Responsive state
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);

    const [pdfModalVisible, setPdfModalVisible] = useState(false);
    const [pdfDataUrl, setPdfDataUrl] = useState('');
    const [pdfPreviewPendingDelivery, setPdfPreviewPendingDelivery] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [company, setCompany] = useState(null);
    const {
        signatureLayout,
        setSignatureLayout,
        signatureLayoutSaving,
        saveSignatureLayout
    } = useDocumentSignatureLayout('pending_delivery', company, setCompany);

    useEffect(() => {
        if (!pdfModalVisible || !pdfPreviewPendingDelivery) return undefined;

        let active = true;
        setPdfLoading(true);
        setPdfDataUrl('');
        generatePendingDeliveryPDF(pdfPreviewPendingDelivery, company || {}, 'include', undefined, signatureLayout)
            .then(dataUrl => {
                if (active) setPdfDataUrl(dataUrl);
            })
            .catch(error => {
                console.error('Pending delivery PDF preview error:', error);
                if (active) message.error('ไม่สามารถสร้างตัวอย่างใบค้างส่งได้: ' + error.message);
            })
            .finally(() => {
                if (active) setPdfLoading(false);
            });

        return () => { active = false; };
    }, [pdfModalVisible, pdfPreviewPendingDelivery, company, signatureLayout]);

    const handleSaveSignatureLayout = async () => {
        try {
            await saveSignatureLayout();
            message.success('บันทึกตำแหน่งลายเซ็นสำหรับใบค้างส่งแล้ว');
        } catch (error) {
            message.error(error.response?.data?.message || 'ไม่สามารถบันทึกตำแหน่งลายเซ็นได้');
        }
    };

    // Pagination and filters
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        customer_id: '',
    });
    const [statusCounts, setStatusCounts] = useState([]);

    // Status options and colors
    const statusOptions = [
        { value: 'pending', label: 'ค้างส่ง', color: 'error', icon: <ClockCircleOutlined /> },
        { value: 'partial', label: 'ส่งบางส่วน', color: 'warning', icon: <ExclamationCircleOutlined /> },
        { value: 'completed', label: 'ส่งครบแล้ว', color: 'success', icon: <CheckCircleOutlined /> },
        { value: 'cancelled', label: 'ยกเลิก', color: 'default', icon: <CloseCircleOutlined /> },
    ];

    const getStatusColor = (status) => {
        const statusConfig = statusOptions.find(opt => opt.value === status);
        return statusConfig?.color || 'default';
    };

    const getStatusLabel = (status) => {
        const statusConfig = statusOptions.find(opt => opt.value === status);
        return statusConfig?.label || status;
    };

    // Custom filter function for contact selection
    const customContactFilter = (input, option) => {
        if (!input) return true;

        let searchText = '';

        if (typeof option.children === 'string') {
            searchText = option.children;
        } else if (React.isValidElement(option.children)) {
            searchText = option.children.props?.children || '';
        } else if (Array.isArray(option.children)) {
            searchText = option.children.map(child =>
                typeof child === 'string' ? child : ''
            ).join(' ');
        }

        if (option.label && typeof option.label === 'string') {
            searchText += ' ' + option.label;
        }

        return searchText.toLowerCase().includes(input.toLowerCase());
    };

    // Load data
    useEffect(() => {
        fetchCompanySettings();
        loadDeliveryNotes();
        loadContacts();
        loadProducts();
    }, [pagination.current, pagination.pageSize, filters]);

    const fetchCompanySettings = async () => {
        try {
            const response = await getCompanySettings();
            if (response.data.success) {
                setCompany(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching company settings:', error);
        }
    };

    const loadDeliveryNotes = async () => {
        try {
            setLoading(true);
            const response = await getPendingDeliveries({
                page: pagination.current,
                limit: pagination.pageSize,
                ...filters
            });

            if (response.success) {
                setDeliveryNotes(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.pagination.total,
                }));
                setStatusCounts(response.statusCounts || []);
            }
        } catch (error) {
            console.error('Error loading pending deliveries:', error);
            message.error('เกิดข้อผิดพลาดในการโหลดข้อมูล');
        } finally {
            setLoading(false);
        }
    };

    const loadContacts = async () => {
        try {
            const response = await getCustomersForDropdown();
            if (response.success) {
                setContacts(response.data);
            }
        } catch (error) {
            console.error('Error loading contacts:', error);
        }
    };

    const loadProducts = async () => {
        try {
            const response = await getProducts(1, 1000);
            if (response.success) {
                setProducts(response.data);
            }
        } catch (error) {
            console.error('Error loading products:', error);
        }
    };

    // Handle table changes
    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    // Handle search
    const handleSearch = (value) => {
        setFilters(prev => ({ ...prev, search: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Clear filters
    const clearFilters = () => {
        setFilters({ search: '', status: '', customer_id: '' });
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Handle modal operations
    const showCreateModal = () => {
        setEditingDeliveryNote(null);
        setModalVisible(true);
        form.resetFields();
        form.setFieldsValue({
            issue_date: dayjs(),
            expected_date: dayjs().add(7, 'day'),
            vat_type: 'exclude_vat',
            discount_percent: 0,
            status: 'pending',
            items: [{ description: '', quantity: 1, delivered_quantity: 0, unit_price: 0, unit: 'กล่อง', is_free: false }]
        });
    };

    const showEditModal = async (deliveryNote) => {
        try {
            const response = await getPendingDeliveryById(deliveryNote.id);
            if (response.success) {
                setEditingDeliveryNote(response.data);
                setModalVisible(true);
                form.setFieldsValue({
                    customer_id: response.data.customer_id,
                    issue_date: dayjs(response.data.issue_date),
                    expected_date: response.data.expected_date ? dayjs(response.data.expected_date) : null,
                    vat_type: response.data.vat_type || 'include',
                    discount_percent: response.data.discount_percent || 0,
                    status: response.data.status,
                    notes: response.data.notes,
                    items: response.data.items?.map(item => ({
                        ...item,
                        unit: item.unit || 'กล่อง',
                        is_free: item.is_free || false
                    })) || [{ description: '', quantity: 1, delivered_quantity: 0, unit_price: 0, unit: 'กล่อง', is_free: false }]
                });
            }
        } catch (error) {
            message.error('เกิดข้อผิดพลาดในการโหลดข้อมูลใบแจ้งสินค้าค้างส่ง');
        }
    };

    const showDetailDrawer = async (deliveryNote) => {
        try {
            setLoading(true);
            const response = await getPendingDeliveryById(deliveryNote.id);
            if (response.success) {
                setSelectedDeliveryNote(response.data);
                setDetailDrawerVisible(true);
            }
        } catch (error) {
            message.error('เกิดข้อผิดพลาดในการโหลดรายละเอียดใบแจ้งสินค้าค้างส่ง');
        } finally {
            setLoading(false);
        }
    };

    const showDeliveryModal = async (deliveryNote) => {
        try {
            const response = await getPendingDeliveryById(deliveryNote.id);
            if (response.success) {
                setSelectedDeliveryNote(response.data);
                const initialValues = {};
                (response.data.items || []).forEach((item) => {
                    initialValues[`delivered_${item.id}`] = item.delivered_quantity || 0;
                });
                deliveryForm.setFieldsValue(initialValues);
                setDeliveryModalVisible(true);
            }
        } catch (error) {
            message.error('เกิดข้อผิดพลาดในการโหลดข้อมูล');
        }
    };

    // Handle form submission
    const handleSubmit = async (values) => {
        try {
            setLoading(true);

            const formData = {
                ...values,
                issue_date: values.issue_date.format('YYYY-MM-DD'),
                expected_date: values.expected_date ? values.expected_date.format('YYYY-MM-DD') : null,
                vat_type: values.vat_type || 'exclude_vat',
            };

            if (editingDeliveryNote) {
                await updatePendingDelivery(editingDeliveryNote.id, formData);
                message.success('อัปเดตใบแจ้งสินค้าค้างส่งเรียบร้อยแล้ว');
            } else {
                await createPendingDelivery(formData);
                message.success('สร้างใบแจ้งสินค้าค้างส่งเรียบร้อยแล้ว');
            }

            setModalVisible(false);
            loadDeliveryNotes();
        } catch (error) {
            message.error(error.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
        } finally {
            setLoading(false);
        }
    };

    const handleDeliverySubmit = async (values) => {
        try {
            const updateItems = selectedDeliveryNote.items.map(item => ({
                item_id: item.id,
                delivered_quantity: values[`delivered_${item.id}`] || 0
            }));

            await updateDeliveredQuantity(selectedDeliveryNote.id, updateItems);
            message.success('อัปเดตจำนวนที่ส่งไปแล้วเรียบร้อยแล้ว');
            setDeliveryModalVisible(false);
            loadDeliveryNotes();
            if (detailDrawerVisible) {
                showDetailDrawer(selectedDeliveryNote);
            }
        } catch (error) {
            message.error(error.message || 'เกิดข้อผิดพลาดในการอัปเดตข้อมูล');
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        try {
            await deletePendingDelivery(id);
            message.success('ลบใบแจ้งสินค้าค้างส่งเรียบร้อยแล้ว');
            loadDeliveryNotes();
        } catch (error) {
            message.error(error.message || 'เกิดข้อผิดพลาดในการลบข้อมูล');
        }
    };

    // Handle status update
    const handleStatusUpdate = async (deliveryNote, newStatus) => {
        try {
            await updatePendingDelivery(deliveryNote.id, { status: newStatus });
            message.success('อัปเดตสถานะเรียบร้อยแล้ว');
            loadDeliveryNotes();
            if (selectedDeliveryNote && selectedDeliveryNote.id === deliveryNote.id) {
                setSelectedDeliveryNote(prev => ({ ...prev, status: newStatus }));
            }
        } catch (error) {
            message.error('เกิดข้อผิดพลาดในการอัปเดตสถานะ');
        }
    };

    const handlePrintDeliveryNote = async (record) => {
        try {
            message.loading('กำลังสร้าง PDF...', 1);

            const response = await getPendingDeliveryById(record.id);
            console.log('Printing Pending Delivery...', response.data);

            if (response.success) {
                const pendingDeliveryData = response.data;

                // แปลงข้อมูลให้เข้ากับ format ของ PDF service
                const formattedPendingDelivery = {
                    pending_delivery_number: pendingDeliveryData.pending_delivery_number,
                    issue_date: pendingDeliveryData.issue_date,
                    expected_date: pendingDeliveryData.expected_date,
                    customer_name: pendingDeliveryData.customer_name,
                    customer_address: pendingDeliveryData.customer_address || '',
                    customer_phone: pendingDeliveryData.customer_phone || '',
                    customer_tax_id: pendingDeliveryData.customer_tax_id || '',
                    items: pendingDeliveryData.items?.map(item => ({
                        product_name: item.product_name,
                        name: item.product_name,
                        description: item.description,
                        quantity: item.quantity,
                        delivered_quantity: item.delivered_quantity,
                        pending_quantity: item.pending_quantity,
                        unit: item.unit || 'ชิ้น',
                        unit_price: item.unit_price,
                        is_free: item.is_free,
                        barcode: item.barcode
                    })) || [],
                    notes: pendingDeliveryData.notes,
                    status: pendingDeliveryData.status,
                    vat_type: pendingDeliveryData.vat_type,
                    discount_percent: pendingDeliveryData.discount_percent || 0,
                    discount_amount: pendingDeliveryData.discount_amount || 0,
                    subtotal: pendingDeliveryData.subtotal || 0,
                    tax_amount: pendingDeliveryData.tax_amount || 0,
                    total_amount: pendingDeliveryData.total_amount || 0
                };

                setPdfPreviewPendingDelivery(formattedPendingDelivery);
                setPdfModalVisible(true);

            } else {
                message.error('ไม่สามารถโหลดข้อมูลใบแจ้งสินค้าค้างส่งได้');
            }
        } catch (error) {
            console.error('PDF generation error:', error);
            message.error('เกิดข้อผิดพลาดในการสร้าง PDF: ' + error.message);
        }
    };

    // Mobile Card Component
    const DeliveryNoteCard = ({ deliveryNote }) => (
        <Card
            size="small"
            style={{ marginBottom: 16 }}
            actions={[
                <Tooltip title="ดูรายละเอียด">
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => showDetailDrawer(deliveryNote)}
                        size="small"
                    />
                </Tooltip>,
                <Tooltip title="อัปเดตการส่ง">
                    <Button
                        type="text"
                        icon={<CheckCircleOutlined />}
                        onClick={() => showDeliveryModal(deliveryNote)}
                        size="small"
                        disabled={deliveryNote.status === 'completed' || deliveryNote.status === 'cancelled'}
                        style={{ color: '#52c41a' }}
                    />
                </Tooltip>,
                <Tooltip title="แก้ไข">
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => showEditModal(deliveryNote)}
                        size="small"
                    />
                </Tooltip>,
                <Popconfirm
                    title="คุณแน่ใจหรือไม่ที่จะลบใบแจ้งสินค้าค้างส่งนี้?"
                    onConfirm={() => handleDelete(deliveryNote.id)}
                    okText="ใช่"
                    cancelText="ไม่"
                >
                    <Tooltip title="ลบ">
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                        />
                    </Tooltip>
                </Popconfirm>
            ]}
        >

            <div onClick={() => showDetailDrawer(deliveryNote)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong>{deliveryNote.pending_delivery_number}</strong>
                    <Tag color={getStatusColor(deliveryNote.status)}>
                        {getStatusLabel(deliveryNote.status)}
                    </Tag>
                </div>

                <div style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{deliveryNote.contact_name}</div>
                    {deliveryNote.company_name && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{deliveryNote.company_name}</div>
                    )}
                </div>

                <Row gutter={16} style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
                    <Col span={12}>
                        <CalendarOutlined style={{ marginRight: 4 }} />
                        {dayjs(deliveryNote.issue_date).format('DD/MM/YYYY')}
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <DollarOutlined style={{ marginRight: 4 }} />
                        ฿{parseFloat(deliveryNote?.total_amount || 0)?.toLocaleString()}
                    </Col>
                </Row>

                <Row gutter={8} style={{ fontSize: '12px' }}>
                    <Col span={8}>
                        <span style={{ color: '#666' }}>ทั้งหมด: </span>
                        <strong>{parseFloat(deliveryNote.total_quantity || 0).toFixed(2)}</strong>
                    </Col>
                    <Col span={8}>
                        <span style={{ color: '#52c41a' }}>ส่งแล้ว: </span>
                        <strong style={{ color: '#52c41a' }}>{parseFloat(deliveryNote.total_delivered || 0).toFixed(2)}</strong>
                    </Col>
                    <Col span={8}>
                        <span style={{ color: '#ff4d4f' }}>ค้าง: </span>
                        <strong style={{ color: '#ff4d4f' }}>{parseFloat(deliveryNote.total_pending || 0).toFixed(2)}</strong>
                    </Col>
                </Row>
            </div>
        </Card>
    );

    const totalAmount = deliveryNotes.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0);
    const totalQuantity = deliveryNotes.reduce((sum, item) => sum + parseFloat(item.total_quantity || 0), 0);
    const totalDelivered = deliveryNotes.reduce((sum, item) => sum + parseFloat(item.total_delivered || 0), 0);
    const totalPending = deliveryNotes.reduce((sum, item) => sum + parseFloat(item.total_pending || 0), 0);

    // Table columns
    const columns = [
        {
            title: 'เลขที่เอกสาร',
            dataIndex: 'pending_delivery_number',
            key: 'pending_delivery_number',
            width: 150,
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => showDetailDrawer(record)}
                    style={{ padding: 0, height: 'auto' }}
                >
                    {text}
                </Button>
            ),
        },
        {
            title: 'ลูกค้า',
            key: 'customer',
            width: 200,
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{record.contact_name}</div>
                    {record.company_name && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{record.company_name}</div>
                    )}
                </div>
            ),
        },
        {
            title: 'วันที่',
            dataIndex: 'issue_date',
            key: 'issue_date',
            width: 120,
            render: (date) => dayjs(date).format('DD/MM/YYYY'),
        },
        {
            title: 'กำหนดส่ง',
            dataIndex: 'expected_date',
            key: 'expected_date',
            width: 120,
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'จำนวนทั้งหมด',
            dataIndex: 'total_quantity',
            key: 'total_quantity',
            width: 120,
            align: 'right',
            render: (value) => parseFloat(value || 0).toFixed(2),
        },
        {
            title: 'ส่งแล้ว',
            dataIndex: 'total_delivered',
            key: 'total_delivered',
            width: 120,
            align: 'right',
            render: (value) => (
                <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                    {parseFloat(value || 0).toFixed(2)}
                </span>
            ),
        },
        {
            title: 'ค้างส่ง',
            dataIndex: 'total_pending',
            key: 'total_pending',
            width: 120,
            align: 'right',
            render: (value) => (
                <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                    {parseFloat(value || 0).toFixed(2)}
                </span>
            ),
        },
        {
            title: 'ยอดรวม',
            dataIndex: 'total_amount',
            key: 'total_amount',
            width: 120,
            align: 'right',
            render: (amount) => `฿${parseFloat(amount || 0).toLocaleString()}`,
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusLabel(status)}
                </Tag>
            ),
        },
        {
            title: 'การดำเนินการ',
            key: 'actions',
            width: 250,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="ดูรายละเอียด">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => showDetailDrawer(record)}
                            size="small"
                        />
                    </Tooltip>

                    <Tooltip title="อัปเดตการส่ง">
                        <Button
                            type="text"
                            icon={<CheckCircleOutlined />}
                            onClick={() => showDeliveryModal(record)}
                            size="small"
                            disabled={record.status === 'completed' || record.status === 'cancelled'}
                            style={{ color: record.status === 'completed' || record.status === 'cancelled' ? undefined : '#52c41a' }}
                        />
                    </Tooltip>

                    <Tooltip title="พิมพ์ PDF">
                        <Button
                            type="text"
                            icon={<PrinterOutlined />}
                            onClick={() => handlePrintDeliveryNote(record)}
                            size="small"
                            style={{ color: '#722ed1' }}
                        />
                    </Tooltip>

                    <Tooltip title="แก้ไข">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => showEditModal(record)}
                            size="small"
                        />
                    </Tooltip>

                    <Popconfirm
                        title="คุณแน่ใจหรือไม่ที่จะลบใบแจ้งสินค้าค้างส่งนี้?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="ใช่"
                        cancelText="ไม่"
                    >
                        <Tooltip title="ลบ">
                            <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                danger
                                size="small"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px' }}>
            <div style={{ marginBottom: '24px' }}>
                <Title level={2}>
                    <TruckOutlined style={{ marginRight: '8px' }} />
                    จัดการใบแจ้งสินค้าค้างส่ง
                </Title>
            </div>

            {/* Modal แสดง PDF */}
            <Modal
                title="ใบแจ้งสินค้าค้างส่ง"
                open={pdfModalVisible}
                onCancel={() => setPdfModalVisible(false)}
                footer={null}
                width={isMobile ? '100%' : 900}
                bodyStyle={{ height: isMobile ? '80vh' : '700px', padding: 0, display: 'flex', flexDirection: 'column' }}
                destroyOnClose
            >
                <SignatureLayoutControls
                    documentType="pending_delivery"
                    value={signatureLayout}
                    onChange={setSignatureLayout}
                    onSave={handleSaveSignatureLayout}
                    saving={signatureLayoutSaving}
                />
                {pdfLoading ? <Spin size="large" style={{ margin: 'auto' }} /> : pdfDataUrl && (
                    <iframe
                        src={pdfDataUrl}
                        style={{ width: '100%', flex: 1, minHeight: 0, border: 'none' }}
                        title="ตัวอย่างใบแจ้งสินค้าค้างส่ง"
                    />
                )}
            </Modal>


            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                {statusCounts.map(item => (
                    <Col xs={12} sm={8} md={6} lg={4} key={item.status}>
                        <Card size={isMobile ? "small" : "default"}>
                            <Statistic
                                title={getStatusLabel(item.status)}
                                value={item.count}
                                valueStyle={{
                                    fontSize: isMobile ? '20px' : '24px',
                                    color: getStatusColor(item.status) === 'success' ? '#3f8600' :
                                        getStatusColor(item.status) === 'error' ? '#cf1322' :
                                            getStatusColor(item.status) === 'warning' ? '#d48806' : '#1890ff'
                                }}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Desktop Filters and Search */}
            {!isMobile && (
                <Card style={{ marginBottom: '24px' }}>
                    <Row gutter={16} align="middle">
                        <Col flex="300px">
                            <Input
                                placeholder="ค้นหาเลขที่เอกสาร หรือ ชื่อลูกค้า"
                                prefix={<SearchOutlined />}
                                value={filters.search}
                                onChange={(e) => handleSearch(e.target.value)}
                                allowClear
                            />
                        </Col>
                        <Col flex="200px">
                            <Select
                                placeholder="เลือกสถานะ"
                                value={filters.status}
                                onChange={(value) => handleFilterChange('status', value)}
                                allowClear
                                style={{ width: '100%' }}
                            >
                                {statusOptions.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col flex="200px">
                            <Select
                                placeholder="เลือกลูกค้า"
                                value={filters.customer_id}
                                onChange={(value) => handleFilterChange('customer_id', value)}
                                allowClear
                                showSearch
                                filterOption={customContactFilter}
                                style={{ width: '100%' }}
                            >
                                {contacts.map(contact => (
                                    <Option
                                        key={contact.id}
                                        value={contact.id}
                                        label={`${contact.name}${contact.company_name ? ` (${contact.company_name})` : ''}`}
                                    >
                                        {contact.name} {contact.company_name && `(${contact.company_name})`}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col>
                            <Space>
                                <Button onClick={clearFilters}>ล้างตัวกรอง</Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={showCreateModal}
                                >
                                    สร้างใบแจ้งสินค้าค้างส่ง
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            )}

            {/* Mobile Search and Filters */}
            {isMobile && (
                <>
                    <Card style={{ marginBottom: '16px' }}>
                        <Input
                            placeholder="ค้นหาเลขที่เอกสาร หรือ ชื่อลูกค้า"
                            prefix={<SearchOutlined />}
                            value={filters.search}
                            onChange={(e) => handleSearch(e.target.value)}
                            allowClear
                        />
                        <div style={{ marginTop: '12px', textAlign: 'center' }}>
                            <Button
                                icon={<FilterOutlined />}
                                onClick={() => setMobileFiltersVisible(true)}
                                style={{ marginRight: '8px' }}
                            >
                                ตัวกรอง
                            </Button>
                        </div>
                    </Card>

                    <Drawer
                        title="ตัวกรองข้อมูล"
                        placement="bottom"
                        height="auto"
                        open={mobileFiltersVisible}
                        onClose={() => setMobileFiltersVisible(false)}
                    >
                        <div style={{ padding: '16px 0' }}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>สถานะ</label>
                                <Select
                                    placeholder="เลือกสถานะ"
                                    value={filters.status}
                                    onChange={(value) => handleFilterChange('status', value)}
                                    allowClear
                                    style={{ width: '100%' }}
                                >
                                    {statusOptions.map(option => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>ลูกค้า</label>
                                <Select
                                    placeholder="เลือกลูกค้า"
                                    value={filters.customer_id}
                                    onChange={(value) => handleFilterChange('customer_id', value)}
                                    allowClear
                                    showSearch
                                    filterOption={customContactFilter}
                                    style={{ width: '100%' }}
                                >
                                    {contacts.map(contact => (
                                        <Option key={contact.id} value={contact.id}>
                                            {contact.name} {contact.company_name && `(${contact.company_name})`}
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div style={{ textAlign: 'center', paddingTop: '16px' }}>
                                <Space>
                                    <Button onClick={clearFilters}>ล้างตัวกรอง</Button>
                                    <Button
                                        type="primary"
                                        onClick={() => setMobileFiltersVisible(false)}
                                    >
                                        ตกลง
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    </Drawer>
                </>
            )}

            {/* Data Display */}
            <Card>
                {isMobile ? (
                    <List
                        loading={loading}
                        dataSource={deliveryNotes}
                        renderItem={(deliveryNote) => <DeliveryNoteCard key={deliveryNote.id} deliveryNote={deliveryNote} />}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            onChange: (page, pageSize) => {
                                setPagination(prev => ({ ...prev, current: page, pageSize }));
                            },
                            showSizeChanger: false,
                            showQuickJumper: false,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} จาก ${total} รายการ`,
                            size: 'small'
                        }}
                    />
                ) : (
                    <Table
                        columns={columns}
                        dataSource={deliveryNotes}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} จาก ${total} รายการ`,
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 1500 }}
                        summary={() => (
                            <Table.Summary>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={4}>
                                        <strong>รวมทั้งหมด</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} align="right">
                                        <strong>{totalQuantity.toFixed(2)}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="right">
                                        <strong style={{ color: '#52c41a' }}>{totalDelivered.toFixed(2)}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={6} align="right">
                                        <strong style={{ color: '#ff4d4f' }}>{totalPending.toFixed(2)}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={7} align="right">
                                        <strong>฿{totalAmount.toLocaleString()}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={8} />
                                    <Table.Summary.Cell index={9} />
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                )}
            </Card>

            {/* Float Button for Mobile Add */}
            {isMobile && (
                <FloatButton
                    icon={<PlusOutlined />}
                    type="primary"
                    onClick={showCreateModal}
                    style={{ right: 24, bottom: 24 }}
                />
            )}

            {/* Create/Edit Form */}
            {isMobile ? (
                <Drawer
                    maskClosable={false}
                    title={editingDeliveryNote ? 'แก้ไขใบแจ้งสินค้าค้างส่ง' : 'สร้างใบแจ้งสินค้าค้างส่งใหม่'}
                    open={modalVisible}
                    onClose={() => setModalVisible(false)}
                    width="100%"
                    placement="right"
                    destroyOnClose
                    extra={
                        <Space>
                            <Button onClick={() => setModalVisible(false)}>ยกเลิก</Button>
                            <Button
                                type="primary"
                                onClick={() => form.submit()}
                                loading={loading}
                            >
                                บันทึก
                            </Button>
                        </Space>
                    }
                >
                    <DeliveryNoteForm
                        form={form}
                        onFinish={handleSubmit}
                        contacts={contacts}
                        products={products}
                        loading={loading}
                        onCancel={() => setModalVisible(false)}
                        isMobile={isMobile}
                    />
                </Drawer>
            ) : (
                <Modal
                    maskClosable={false}
                    title={editingDeliveryNote ? 'แก้ไขใบแจ้งสินค้าค้างส่ง' : 'สร้างใบแจ้งสินค้าค้างส่งใหม่'}
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    footer={null}
                    width={1200}
                    destroyOnClose
                >
                    <DeliveryNoteForm
                        form={form}
                        onFinish={handleSubmit}
                        contacts={contacts}
                        products={products}
                        loading={loading}
                        onCancel={() => setModalVisible(false)}
                        isMobile={isMobile}
                    />
                </Modal>
            )}

            {/* Delivery Update Modal */}
            <Modal
                title={`อัปเดตการส่ง: ${selectedDeliveryNote?.pending_delivery_number || ''}`}
                open={deliveryModalVisible}
                onCancel={() => setDeliveryModalVisible(false)}
                onOk={() => deliveryForm.submit()}
                width={isMobile ? '100%' : 900}
                okText="บันทึก"
                cancelText="ยกเลิก"
            >
                {selectedDeliveryNote && (
                    <Form form={deliveryForm} onFinish={handleDeliverySubmit}>
                        <Table
                            dataSource={selectedDeliveryNote.items || []}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            scroll={{ x: 'max-content' }}
                            columns={[
                                {
                                    title: 'รายการ',
                                    dataIndex: 'description',
                                    width: isMobile ? 150 : 250
                                },
                                {
                                    title: 'จำนวนทั้งหมด',
                                    dataIndex: 'quantity',
                                    width: 120,
                                    align: 'right',
                                    render: (v, r) => `${parseFloat(v || 0).toFixed(2)} ${r.unit || ''}`
                                },
                                {
                                    title: 'ส่งแล้ว (เดิม)',
                                    dataIndex: 'delivered_quantity',
                                    width: 120,
                                    align: 'right',
                                    render: v => <span style={{ color: '#52c41a' }}>{parseFloat(v || 0).toFixed(2)}</span>
                                },
                                {
                                    title: 'อัปเดตจำนวนที่ส่ง',
                                    key: 'delivered_input',
                                    width: 150,
                                    render: (_, item) => (
                                        <Form.Item
                                            name={`delivered_${item.id}`}
                                            style={{ margin: 0 }}
                                            rules={[
                                                { required: true, message: 'กรุณากรอกจำนวน' },
                                                {
                                                    validator: (_, v) =>
                                                        v >= 0 && v <= item.quantity
                                                            ? Promise.resolve()
                                                            : Promise.reject('จำนวนไม่ถูกต้อง')
                                                }
                                            ]}
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                min={0}
                                                max={item.quantity}
                                                precision={2}
                                            />
                                        </Form.Item>
                                    )
                                },
                                {
                                    title: 'คงค้าง',
                                    key: 'pending',
                                    width: 100,
                                    align: 'right',
                                    render: (_, item) => {
                                        const delivered = deliveryForm.getFieldValue(`delivered_${item.id}`) || 0;
                                        const pending = item.quantity - delivered;
                                        return (
                                            <span style={{ color: pending > 0 ? '#ff4d4f' : '#52c41a', fontWeight: 'bold' }}>
                                                {parseFloat(pending).toFixed(2)}
                                            </span>
                                        );
                                    }
                                }
                            ]}
                        />
                    </Form>
                )}
            </Modal>

            {/* Detail Drawer */}
            <Drawer
                title="รายละเอียดใบแจ้งสินค้าค้างส่ง"
                open={detailDrawerVisible}
                onClose={() => setDetailDrawerVisible(false)}
                width={isMobile ? "100%" : 600}
                extra={
                    selectedDeliveryNote && (
                        <Space>
                            <Select
                                value={selectedDeliveryNote.status}
                                onChange={(value) => handleStatusUpdate(selectedDeliveryNote, value)}
                                style={{ width: 150 }}
                            >
                                {statusOptions.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Space>
                    )
                }
            >
                {selectedDeliveryNote && <DeliveryNoteDetail deliveryNote={selectedDeliveryNote} isMobile={isMobile} />}
            </Drawer>

        </div>
    );
};

// Delivery Note Form Component พร้อม VAT Mode
const DeliveryNoteForm = ({ form, onFinish, contacts, products, loading, onCancel, isMobile }) => {
    const [vatType, setVatType] = useState('exclude_vat');
    const [calcValues, setCalcValues] = useState({
        subtotal: 0,
        discount_amount: 0,
        tax_amount: 0,
        total_amount: 0
    });

    // หน่วยนับมาตรฐาน - ตอนนี้ใช้ API แทน hardcoded array
    // หน่วยนับจะถูกโหลดจาก /api/units ผ่าน UnitsSelect component

    const customContactFilter = (input, option) => {
        if (!input) return true;
        let searchText = '';
        if (typeof option.children === 'string') {
            searchText = option.children;
        } else if (React.isValidElement(option.children)) {
            searchText = option.children.props?.children || '';
        } else if (Array.isArray(option.children)) {
            searchText = option.children.map(child =>
                typeof child === 'string' ? child : ''
            ).join(' ');
        }
        return searchText.toLowerCase().includes(input.toLowerCase());
    };

    const calculateTotals = (items, discount_percent = 0, vatType = 'exclude_vat') => {
        const itemsSubtotal = items.reduce((sum, item) => {
            if (item?.is_free === true || item?.is_free === 1) {
                return sum;
            }
            return sum + ((item?.quantity || 0) * (item?.unit_price || 0));
        }, 0);

        let subtotal, tax_amount, total_amount;
        const discount_amount = itemsSubtotal * ((discount_percent || 0) / 100);
        const afterDiscount = itemsSubtotal - discount_amount;

        if (vatType === 'include') {
            total_amount = afterDiscount;
            subtotal = total_amount / 1.07;
            tax_amount = total_amount - subtotal;
        } else if (vatType === 'exclude') {
            subtotal = afterDiscount;
            tax_amount = subtotal * 0.07;
            total_amount = subtotal + tax_amount;
        } else {
            subtotal = afterDiscount;
            tax_amount = 0;
            total_amount = afterDiscount;
        }

        return {
            subtotal: Math.round(subtotal * 100) / 100,
            discount_amount: Math.round(discount_amount * 100) / 100,
            tax_amount: Math.round(tax_amount * 100) / 100,
            total_amount: Math.round(total_amount * 100) / 100
        };
    };

    const handleFormChange = () => {
        const items = form.getFieldValue('items') || [];
        const discount_percent = form.getFieldValue('discount_percent') || 0;
        const currentVatType = form.getFieldValue('vat_type') || 'include';

        setVatType(currentVatType);
        const calculations = calculateTotals(items, discount_percent, currentVatType);
        setCalcValues(calculations);
    };

    const handleFinish = (values) => {
        const calculations = calculateTotals(
            values.items || [],
            values.discount_percent || 0,
            values.vat_type || 'include'
        );

        const enhancedValues = {
            ...values,
            subtotal: calcValues.subtotal,
            discount_amount: calcValues.discount_amount,
            tax_amount: calcValues.tax_amount,
            total_amount: calcValues.total_amount
        };

        onFinish(enhancedValues);
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            onValuesChange={handleFormChange}
            initialValues={{
                issue_date: dayjs(),
                expected_date: dayjs().add(7, 'day'),
                vat_type: 'include',
                discount_percent: 0,
                status: 'pending',
                items: [{ description: '', quantity: 1, delivered_quantity: 0, unit_price: 0, unit: 'ชิ้น', is_free: false }]
            }}
        >
            <Row gutter={isMobile ? [0, 16] : 16}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="customer_id"
                        label="ลูกค้า"
                        rules={[{ required: true, message: 'กรุณาเลือกลูกค้า' }]}
                    >
                        <Select
                            placeholder="เลือกลูกค้า"
                            showSearch
                            filterOption={customContactFilter}
                        >
                            {contacts.map(contact => (
                                <Option key={contact.id} value={contact.id}>
                                    {contact.name} {contact.company_name && `(${contact.company_name})`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                    <Form.Item
                        name="issue_date"
                        label="วันที่"
                        rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}
                    >
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                    <Form.Item
                        name="expected_date"
                        label="กำหนดส่ง"
                    >
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                </Col>
            </Row>

            <Divider>รายการสินค้า</Divider>

            <Form.List name="items">
                {(fields, { add, remove }) => (
                    <>
                        {fields.map((field, index) => (
                            <Card
                                key={field.key}
                                size="small"
                                title={`รายการที่ ${index + 1}`}
                                extra={
                                    fields.length > 1 && (
                                        <Button
                                            type="link"
                                            danger
                                            onClick={() => remove(field.name)}
                                        >
                                            ลบ
                                        </Button>
                                    )
                                }
                                style={{ marginBottom: 16 }}
                            >
                                <Row gutter={isMobile ? [0, 16] : 16}>
                                    <Col xs={24} sm={8}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'product_id']}
                                            label="สินค้า"
                                        >
                                            <Select
                                                placeholder="เลือกสินค้า (ไม่บังคับ)"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) => {
                                                    if (!input) return true;
                                                    const searchText = typeof option.children === 'string'
                                                        ? option.children
                                                        : '';
                                                    return searchText.toLowerCase().includes(input.toLowerCase());
                                                }}
                                                onChange={(value, option) => {
                                                    if (value && option) {
                                                        const currentItems = form.getFieldValue('items');
                                                        const product = products.find(p => p.id === value);
                                                        if (product) {
                                                            currentItems[index] = {
                                                                ...currentItems[index],
                                                                product_id: value,
                                                                product_name: product.name,
                                                                // description: product.description,
                                                                unit: product.unit || 'ชิ้น',
                                                                unit_price: product.price || 0
                                                            };
                                                            form.setFieldsValue({ items: currentItems });
                                                            handleFormChange();
                                                        }
                                                    }
                                                }}
                                            >
                                                {products.map(product => (
                                                    <Option key={product.id} value={product.id}>
                                                        {product.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'is_free']}
                                            valuePropName="checked"
                                            initialValue={false}
                                        >
                                            <div style={{ paddingTop: isMobile ? 0 : 30 }}>
                                                <Checkbox
                                                    danger
                                                    onChange={(e) => {
                                                        const currentItems = form.getFieldValue('items');
                                                        currentItems[index].is_free = e.target.checked;
                                                        form.setFieldsValue({ items: currentItems });
                                                        handleFormChange();
                                                    }}
                                                    checked={form.getFieldValue('items')?.[index]?.is_free || false}
                                                >
                                                    <span style={{ color: 'red' }}>รายการแถมฟรี !</span>
                                                </Checkbox>
                                            </div>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'description']}
                                            label="รายละเอียด"
                                            rules={[{ required: false, message: 'กรุณากรอกรายละเอียด' }]}
                                        >
                                            <Input placeholder="รายละเอียดสินค้า/บริการ" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={isMobile ? [0, 16] : 16}>
                                    <Col xs={12} sm={6}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'quantity']}
                                            label="จำนวน"
                                            rules={[{ required: true, message: 'กรุณากรอกจำนวน' }]}
                                        >
                                            <InputNumber
                                                min={0.01}
                                                step={0.01}
                                                style={{ width: '100%' }}
                                                placeholder="จำนวน"
                                                onChange={handleFormChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'delivered_quantity']}
                                            label="ส่งแล้ว"
                                            initialValue={0}
                                        >
                                            <InputNumber
                                                min={0}
                                                step={0.01}
                                                style={{ width: '100%' }}
                                                placeholder="ส่งแล้ว"
                                                onChange={handleFormChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'unit']}
                                            label="หน่วยนับ"
                                            rules={[{ required: true, message: 'กรุณาเลือกหน่วยนับ' }]}
                                        >
                                            <UnitsSelect
                                                placeholder="เลือกหน่วยนับ"
                                                showSearch
                                                allowClear
                                                showRefresh
                                                valueField="unit_name"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'unit_price']}
                                            label="ราคาต่อหน่วย"
                                            rules={[{ required: true, message: 'กรุณากรอกราคา' }]}
                                        >
                                            <InputNumber
                                                min={0}
                                                step={0.01}
                                                style={{ width: '100%' }}
                                                placeholder="ราคาต่อหน่วย"
                                                formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={value => value.replace(/฿\s?|(,*)/g, '')}
                                                onChange={handleFormChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={6}>
                                        <Form.Item label="รวม">
                                            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => {
                                                const prevItems = prevValues.items || [];
                                                const currentItems = currentValues.items || [];
                                                return JSON.stringify(prevItems) !== JSON.stringify(currentItems);
                                            }}>
                                                {({ getFieldValue }) => {
                                                    const items = getFieldValue('items') || [];
                                                    const item = items[index] || {};
                                                    const total = item.is_free === true ? 0 : (item.quantity || 0) * (item.unit_price || 0);
                                                    return (
                                                        <InputNumber
                                                            value={total}
                                                            disabled
                                                            style={{
                                                                width: '100%',
                                                                color: item.is_free === true ? '#52c41a' : undefined
                                                            }}
                                                            formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                        />
                                                    );
                                                }}
                                            </Form.Item>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        ))}
                        <Button
                            type="dashed"
                            onClick={() => add({ description: '', quantity: 1, delivered_quantity: 0, unit_price: 0, unit: 'กล่อง', is_free: false })}
                            block
                            icon={<PlusOutlined />}
                        >
                            เพิ่มรายการ
                        </Button>
                    </>
                )}
            </Form.List>

            <Divider>การคำนวณภาษี</Divider>

            <Row gutter={isMobile ? [0, 16] : 16}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="vat_type"
                        label={
                            <span>
                                <CalculatorOutlined style={{ marginRight: 8 }} />
                                โหมดการคำนวณภาษี
                            </span>
                        }
                        rules={[{ required: true, message: 'กรุณาเลือกโหมดการคำนวณภาษี' }]}
                    >
                        <Radio.Group onChange={handleFormChange}>
                            <Radio.Button value="exclude">VAT EXCLUDE (ยังไม่รวมภาษี)</Radio.Button>
                            <Radio.Button value="include">VAT INCLUDE (รวมภาษีแล้ว)</Radio.Button>
                            <Radio.Button value="no_vat">ไม่มี VAT</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="discount_percent"
                        label="ส่วนลด (%)"
                    >
                        <InputNumber
                            min={0}
                            max={100}
                            step={0.01}
                            style={{ width: '100%' }}
                            onChange={handleFormChange}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Alert
                message={
                    vatType === 'include'
                        ? "โหมด VAT INCLUDE: ราคาสินค้าที่ป้อนรวมภาษีแล้ว ระบบจะแยกคำนวณภาษีออกจากยอดรวม"
                        : vatType === 'exclude'
                            ? "โหมด VAT EXCLUDE: ราคาสินค้าที่ป้อนยังไม่รวมภาษี ระบบจะคำนวณภาษีเพิ่มเติม"
                            : "โหมด NO VAT: ไม่มีการคำนวณภาษี"
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Row gutter={isMobile ? [0, 16] : 16}>
                <Col xs={24} sm={12}>
                    <Form.Item
                        name="status"
                        label="สถานะ"
                        rules={[{ required: true, message: 'กรุณาเลือกสถานะ' }]}
                    >
                        <Select>
                            <Option value="pending">ค้างส่ง</Option>
                            <Option value="partial">ส่งบางส่วน</Option>
                            <Option value="completed">ส่งครบแล้ว</Option>
                            <Option value="cancelled">ยกเลิก</Option>
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Card title="สรุปการคำนวณ" style={{ marginBottom: 16 }}>
                <Row>
                    <Col span={12}><strong>ยอดก่อนส่วนลด:</strong></Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        ฿{(calcValues.subtotal + calcValues.discount_amount)?.toLocaleString()}
                    </Col>
                </Row>
                {calcValues.discount_amount > 0 && (
                    <Row>
                        <Col span={12}><strong>ส่วนลด:</strong></Col>
                        <Col span={12} style={{ textAlign: 'right' }}>-฿{calcValues.discount_amount?.toLocaleString()}</Col>
                    </Row>
                )}
                <Row>
                    <Col span={12}><strong>ยอดก่อนภาษี:</strong></Col>
                    <Col span={12} style={{ textAlign: 'right' }}>฿{calcValues.subtotal?.toLocaleString()}</Col>
                </Row>
                <Row>
                    <Col span={12}><strong>ภาษีมูลค่าเพิ่ม (7%):</strong></Col>
                    <Col span={12} style={{ textAlign: 'right' }}>฿{calcValues.tax_amount?.toLocaleString()}</Col>
                </Row>
                <Divider style={{ margin: '8px 0' }} />
                <Row>
                    <Col span={12}><strong style={{ color: '#1890ff' }}>ยอดรวมทั้งสิ้น:</strong></Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <strong style={{ color: '#1890ff' }}>฿{calcValues.total_amount?.toLocaleString()}</strong>
                    </Col>
                </Row>
            </Card>

            <Form.Item
                name="notes"
                label="หมายเหตุ"
            >
                <TextArea rows={3} placeholder="หมายเหตุเพิ่มเติม" />
            </Form.Item>

            {!isMobile && (
                <Form.Item>
                    <Space>
                        <Button onClick={onCancel}>
                            ยกเลิก
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            บันทึก
                        </Button>
                    </Space>
                </Form.Item>
            )}
        </Form>
    );
};

// Delivery Note Detail Component
const DeliveryNoteDetail = ({ deliveryNote, isMobile }) => {
    const statusOptions = [
        { value: 'pending', label: 'ค้างส่ง', color: 'error' },
        { value: 'partial', label: 'ส่งบางส่วน', color: 'warning' },
        { value: 'completed', label: 'ส่งครบแล้ว', color: 'success' },
        { value: 'cancelled', label: 'ยกเลิก', color: 'default' },
    ];

    const getStatusColor = (status) => {
        const statusConfig = statusOptions.find(opt => opt.value === status);
        return statusConfig?.color || 'default';
    };

    const getStatusLabel = (status) => {
        const statusConfig = statusOptions.find(opt => opt.value === status);
        return statusConfig?.label || status;
    };

    return (
        <Tabs defaultActiveKey="1">
            <TabPane tab="ข้อมูลใบแจ้งสินค้าค้างส่ง" key="1">
                <Descriptions column={isMobile ? 1 : 2} bordered size={isMobile ? "small" : "default"}>
                    <Descriptions.Item label="เลขที่เอกสาร" span={isMobile ? 1 : 2}>
                        {deliveryNote.pending_delivery_number}
                    </Descriptions.Item>
                    <Descriptions.Item label="ลูกค้า" span={isMobile ? 1 : 2}>
                        <div>
                            <div>{deliveryNote.customer_name}</div>
                            {deliveryNote.customer_address && (
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                    {deliveryNote.customer_address}
                                </div>
                            )}
                        </div>
                    </Descriptions.Item>
                    <Descriptions.Item label="วันที่">
                        {dayjs(deliveryNote.issue_date).format('DD/MM/YYYY')}
                    </Descriptions.Item>
                    <Descriptions.Item label="กำหนดส่ง">
                        {deliveryNote.expected_date ? dayjs(deliveryNote.expected_date).format('DD/MM/YYYY') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="สถานะ" span={isMobile ? 1 : 2}>
                        <Tag color={getStatusColor(deliveryNote.status)}>
                            {getStatusLabel(deliveryNote.status)}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="โหมดการคำนวณภาษี" span={isMobile ? 1 : 2}>
                        <Tag color={
                            deliveryNote.vat_type === 'include' ? 'green' :
                                deliveryNote.vat_type === 'exclude' ? 'blue' : 'default'
                        }>
                            {deliveryNote.vat_type === 'include' ? 'VAT INCLUDE (รวมภาษีแล้ว)' :
                                deliveryNote.vat_type === 'exclude' ? 'VAT EXCLUDE (ยังไม่รวมภาษี)' : 'ไม่มี VAT'}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="ยอดรวมก่อนภาษี">
                        ฿{parseFloat(deliveryNote.subtotal || 0)?.toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="ส่วนลด">
                        ฿{parseFloat(deliveryNote.discount_amount || 0)?.toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="ภาษี (7%)">
                        ฿{parseFloat(deliveryNote.tax_amount || 0)?.toLocaleString()}
                    </Descriptions.Item>
                    <Descriptions.Item label="ยอดรวมทั้งสิ้น">
                        <strong>฿{parseFloat(deliveryNote.total_amount || 0)?.toLocaleString()}</strong>
                    </Descriptions.Item>
                    {deliveryNote.notes && (
                        <Descriptions.Item label="หมายเหตุ" span={isMobile ? 1 : 2}>
                            {deliveryNote.notes}
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label="ผู้สร้าง">
                        {deliveryNote.created_by_name}
                    </Descriptions.Item>
                    <Descriptions.Item label="วันที่สร้าง">
                        {dayjs(deliveryNote.created_at).format('DD/MM/YYYY HH:mm')}
                    </Descriptions.Item>
                </Descriptions>
            </TabPane>

            <TabPane tab="รายการสินค้า" key="2">
                {deliveryNote.items && deliveryNote.items.length > 0 ? (
                    isMobile ? (
                        <List
                            dataSource={deliveryNote.items}
                            renderItem={(item, index) => (
                                <Card
                                    size="small"
                                    style={{ marginBottom: 8 }}
                                    title={`รายการที่ ${index + 1}`}
                                    extra={item.is_free ? <Tag color="green">ของแถม</Tag> : null}
                                >
                                    <p><strong>รายการ:</strong> {item.description}</p>
                                    <Row gutter={16}>
                                        <Col span={8}>
                                            <p><strong>จำนวน:</strong> {parseFloat(item.quantity || 0).toFixed(2)} {item.unit || 'กล่อง'}</p>
                                        </Col>
                                        <Col span={8}>
                                            <p style={{ color: '#52c41a' }}>
                                                <strong>ส่งแล้ว:</strong> {parseFloat(item.delivered_quantity || 0).toFixed(2)}
                                            </p>
                                        </Col>
                                        <Col span={8}>
                                            <p style={{ color: '#ff4d4f' }}>
                                                <strong>ค้างส่ง:</strong> {parseFloat(item.pending_quantity || 0).toFixed(2)}
                                            </p>
                                        </Col>
                                        <Col span={12}>
                                            <p><strong>ราคา/หน่วย:</strong> ฿{parseFloat(item.unit_price || 0)?.toLocaleString()}</p>
                                        </Col>
                                    </Row>
                                </Card>
                            )}
                        />
                    ) : (
                        <Table
                            dataSource={deliveryNote.items}
                            rowKey="id"
                            pagination={false}
                            columns={[
                                {
                                    title: 'รายการ',
                                    dataIndex: 'description',
                                    key: 'description',
                                },
                                {
                                    title: 'จำนวนทั้งหมด',
                                    key: 'quantity',
                                    align: 'right',
                                    width: 120,
                                    render: (_, record) => `${parseFloat(record.quantity || 0).toFixed(2)} ${record.unit || 'ชิ้น'}`,
                                },
                                {
                                    title: 'ส่งแล้ว',
                                    dataIndex: 'delivered_quantity',
                                    key: 'delivered_quantity',
                                    align: 'right',
                                    width: 120,
                                    render: (qty) => (
                                        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                            {parseFloat(qty || 0).toFixed(2)}
                                        </span>
                                    ),
                                },
                                {
                                    title: 'ค้างส่ง',
                                    dataIndex: 'pending_quantity',
                                    key: 'pending_quantity',
                                    align: 'right',
                                    width: 120,
                                    render: (qty) => (
                                        <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                                            {parseFloat(qty || 0).toFixed(2)}
                                        </span>
                                    ),
                                },
                                {
                                    title: 'ราคาต่อหน่วย',
                                    dataIndex: 'unit_price',
                                    key: 'unit_price',
                                    align: 'right',
                                    width: 120,
                                    render: (price, record) =>
                                        record.is_free ? <Tag color="green">ฟรี</Tag> : `฿${parseFloat(price || 0)?.toLocaleString()}`,
                                },
                                {
                                    title: 'ของแถม',
                                    dataIndex: 'is_free',
                                    key: 'is_free',
                                    align: 'center',
                                    width: 100,
                                    render: (is_free) => is_free ? <Tag color="green">ของแถม</Tag> : '-',
                                },
                            ]}
                        />
                    )
                ) : (
                    <Empty description="ไม่มีรายการสินค้า" />
                )}
            </TabPane>

        </Tabs>
    );
};

export default PendingDeliveriesPage;
